import { log } from '@livekit/components-core';

/**
 * @public
 */
export type GridLayoutDefinition = {
  /** Column count of the grid layout. */
  columns: number;
  /** Row count of the grid layout. */
  rows: number;
  /** Border radius of the layout. */
  radius: string | undefined;
  /** Specifies if the creator of the room (host) will have their own column. */
  focusHostRow: boolean;
  // # Constraints that have to be meet to use this layout.
  // ## 1. Participant range:
  /** Maximum tiles that fit into this layout. */
  maxTiles: number;
};

export type GridLayoutInfo = {
  /** Layout name (convention `<column_count>x<row_count>`). */
  name: string;
  /** Column count of the layout. */
  columns: number;
  /** Row count of the layout. */
  rows: number;
  /** Border radius of the layout. */
  radius: string | undefined;
  /** Specifies if the creator of the room (host) will have their own column. */
  focusHostRow: boolean;
  // # Constraints that have to be meet to use this layout.
  // ## 1. Participant range:
  /** Maximum tiles that fit into this layout. */
  maxTiles: number;
};

export const GRID_LAYOUTS: GridLayoutDefinition[] = [
  {
    columns: 1,
    rows: 1,
    radius: '0px',
    focusHostRow: false,
    maxTiles: 1,
  },
  {
    columns: 1,
    rows: 2,
    radius: undefined,
    focusHostRow: false,
    maxTiles: 2,
  },
  {
    columns: 1,
    rows: 3,
    radius: undefined,
    focusHostRow: false,
    maxTiles: 3,
  },
  {
    columns: 2,
    rows: 2,
    radius: undefined,
    focusHostRow: false,
    maxTiles: 4,
  },
  {
    columns: 2,
    rows: 2,
    radius: undefined,
    focusHostRow: true,
    maxTiles: 5,
  },
  {
    columns: 2,
    rows: 2,
    radius: undefined,
    focusHostRow: false,
    maxTiles: 6,
  },
  {
    columns: 2,
    rows: 2,
    radius: "11.21px",
    focusHostRow: true,
    maxTiles: 7,
  },
  {
    columns: 2,
    rows: 2,
    radius: "11.21px",
    focusHostRow: false,
    maxTiles: 8,
  },
] as const;

export function selectGridLayout(layoutDefinitions: GridLayoutDefinition[], participantCount: number): GridLayoutInfo {
  if (layoutDefinitions.length < 1) {
    throw new Error('At least one grid layout definition must be provided.');
  }
  const layouts = expandAndSortLayoutDefinitions(layoutDefinitions);
  let layout = layouts.find((l) => l.maxTiles === participantCount);

  if (layout === undefined) {
    layout = layouts[layouts.length - 1];
    if (layout) {
      log.warn(
        `No layout found for: participantCount: ${participantCount}, fallback to biggest available layout (${layout}).`,
      );
    } else {
      throw new Error(`No layout or fallback layout found.`);
    }
  }

  return layout;
}

/**
 * @internal
 */
export function expandAndSortLayoutDefinitions(layouts: GridLayoutDefinition[]): GridLayoutInfo[] {
  return [...layouts]
    .map((layout) => {
      return {
        name: `${layout.columns}x${layout.rows}`,
        columns: layout.columns,
        rows: layout.rows,
        radius: layout.radius,
        focusHostRow: layout.focusHostRow,
        maxTiles: layout.maxTiles,
      } satisfies GridLayoutInfo;
    })
    .sort((a, b) => {
      if (a.maxTiles !== b.maxTiles) {
        return a.maxTiles - b.maxTiles;
      } else {
        return 0;
      }
    });
}
